import {ref} from 'vue';
import {defineStore} from 'pinia';
import {CrmService} from '@/services';
import {useValueDataStore} from './Data';

export const useCrmAccountStore = defineStore('crmAccount', () => {
    const account = ref<any | null>(null);
    const reps = ref<any[]>([]);
    const notes = ref<any[]>([]);
    const activities = ref<any[]>([]);
    const salesOrders = ref<any[]>([]);
    const orderDetails = ref<Record<string, any[]>>({});
    const orderDetailsLoading = ref<Record<string, boolean>>({});
    const orderDetailsError = ref<Record<string, boolean>>({});

    const isAccountLoading = ref(false);
    const isRepsLoading = ref(false);
    const isNotesLoading = ref(false);
    const isActivitiesLoading = ref(false);
    const isSalesOrdersLoading = ref(false);

    const accountError = ref(false);
    const repsError = ref(false);
    const notesError = ref(false);
    const activitiesError = ref(false);
    const salesOrdersError = ref(false);

    let loadedAccountFor: string | undefined;
    let loadedRepsFor: string | undefined;
    let loadedNotesFor: string | undefined;
    let loadedActivitiesFor: string | undefined;
    let loadedSalesOrdersFor: string | undefined;

    function currentSelectionId(): string | undefined {
        return useValueDataStore().selectionId;
    }

    function invalidateIfSelectionChanged(): void {
        const sel = currentSelectionId();
        if (loadedAccountFor && loadedAccountFor !== sel) {
            account.value = null;
            reps.value = [];
            notes.value = [];
            activities.value = [];
            salesOrders.value = [];
            orderDetails.value = {};
            orderDetailsLoading.value = {};
            orderDetailsError.value = {};
            loadedAccountFor = undefined;
            loadedRepsFor = undefined;
            loadedNotesFor = undefined;
            loadedActivitiesFor = undefined;
            loadedSalesOrdersFor = undefined;
        }
    }

    async function ensureAccount(): Promise<void> {
        invalidateIfSelectionChanged();
        const sel = currentSelectionId();
        if (!sel) return;
        if (loadedAccountFor === sel) return;
        if (isAccountLoading.value) return;

        isAccountLoading.value = true;
        accountError.value = false;
        try {
            const result = await new CrmService().getAccountByAccountNumber(sel);
            account.value = result;
            accountError.value = !result;
            loadedAccountFor = sel;
        } catch {
            accountError.value = true;
        } finally {
            isAccountLoading.value = false;
        }
    }

    async function ensureReps(): Promise<void> {
        await ensureAccount();
        const sel = currentSelectionId();
        const id = account.value?.accountid;
        if (!sel || !id) return;
        if (loadedRepsFor === sel) return;
        if (isRepsLoading.value) return;

        isRepsLoading.value = true;
        repsError.value = false;
        try {
            const result = await new CrmService().getRepsForAccount(id);
            reps.value = result || [];
            loadedRepsFor = sel;
        } catch {
            repsError.value = true;
        } finally {
            isRepsLoading.value = false;
        }
    }

    async function ensureNotes(): Promise<void> {
        await ensureAccount();
        const sel = currentSelectionId();
        const id = account.value?.accountid;
        if (!sel || !id) return;
        if (loadedNotesFor === sel) return;
        if (isNotesLoading.value) return;

        isNotesLoading.value = true;
        notesError.value = false;
        try {
            const result = await new CrmService().getNotesForAccount(id);
            notes.value = result || [];
            loadedNotesFor = sel;
        } catch {
            notesError.value = true;
        } finally {
            isNotesLoading.value = false;
        }
    }

    async function ensureActivities(): Promise<void> {
        await ensureAccount();
        const sel = currentSelectionId();
        const id = account.value?.accountid;
        if (!sel || !id) return;
        if (loadedActivitiesFor === sel) return;
        if (isActivitiesLoading.value) return;

        isActivitiesLoading.value = true;
        activitiesError.value = false;
        try {
            const result = await new CrmService().getActivitiesForAccount(id);
            activities.value = result || [];
            loadedActivitiesFor = sel;
        } catch {
            activitiesError.value = true;
        } finally {
            isActivitiesLoading.value = false;
        }
    }

    async function ensureSalesOrders(): Promise<void> {
        await ensureAccount();
        const sel = currentSelectionId();
        const id = account.value?.accountid;
        if (!sel || !id) return;
        if (loadedSalesOrdersFor === sel) return;
        if (isSalesOrdersLoading.value) return;

        isSalesOrdersLoading.value = true;
        salesOrdersError.value = false;
        try {
            const result = await new CrmService().getSalesOrdersForAccount(id);
            salesOrders.value = result || [];
            loadedSalesOrdersFor = sel;
        } catch {
            salesOrdersError.value = true;
        } finally {
            isSalesOrdersLoading.value = false;
        }
    }

    async function ensureOrderDetails(orderId: string): Promise<void> {
        if (!orderId) return;
        if (orderDetails.value[orderId]) return;
        if (orderDetailsLoading.value[orderId]) return;

        orderDetailsLoading.value = {...orderDetailsLoading.value, [orderId]: true};
        orderDetailsError.value = {...orderDetailsError.value, [orderId]: false};
        try {
            const result = await new CrmService().getSalesOrderDetailsForOder(orderId);
            orderDetails.value = {...orderDetails.value, [orderId]: result || []};
        } catch {
            orderDetailsError.value = {...orderDetailsError.value, [orderId]: true};
        } finally {
            orderDetailsLoading.value = {...orderDetailsLoading.value, [orderId]: false};
        }
    }

    return {
        account, reps, notes, activities, salesOrders, orderDetails, orderDetailsLoading, orderDetailsError,
        isAccountLoading, isRepsLoading, isNotesLoading, isActivitiesLoading, isSalesOrdersLoading,
        accountError, repsError, notesError, activitiesError, salesOrdersError,
        ensureAccount, ensureReps, ensureNotes, ensureActivities, ensureSalesOrders, ensureOrderDetails,
    };
});
